import { FormEvent, useState } from 'react';
import { useFirebaseAuth } from '@/providers/FirebaseProvider';
import { sendPasswordResetEmail } from 'firebase/auth';

import logo from '../../assets/images/logo-sami-with-name.svg';
import { PageTitle } from '@/components/nav/PageTitle';
import { Loader } from '@/components/generic/Loader';
import { MessageBox, MessageBoxType } from '@/components/layout/MessageBox';
import { NavLink } from 'react-router';
import { ErrorIcon } from '@/components/icons';
import { useTranslation } from '@/hooks/useTranslation';

export function PasswordResetScreen() {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isFormSubmitted, setIsFormSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const firebaseAuth = useFirebaseAuth();

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    setIsLoading(true);
    setError(null);

    sendPasswordResetEmail(firebaseAuth, email)
      .catch((err) => {
        console.error(err);
        setIsFormSubmitted(true);
        setIsLoading(false);
        setError(t('password_reset.account_not_found'));
      })
      .finally(() => {
        setIsFormSubmitted(true);
        setIsLoading(false);
      });
  };

  return (
    <>
      <PageTitle title={t('password_reset.title')} />

      <div className="w-full h-screen overflow-scroll flex flex-col p-8 items-stretch">
        <div>
          <NavLink to={'/'}>
            <img className="h-12 w-auto" src={logo} alt="Sami logo" />
          </NavLink>
        </div>
        <div className="grow flex gap-8 justify-between items-center self-center max-w-xl">
          <div>
            <h1>{t('password_reset.title')}</h1>
            <p className="text-gray-500">{t('password_reset.description')}</p>

            {isFormSubmitted ? (
              <MessageBox type={MessageBoxType.Info}>
                {t('password_reset.email_sent')}
              </MessageBox>
            ) : (
              <div className="mt-8">
                <form
                  name="loginForm"
                  className="space-y-6"
                  onSubmit={handleSubmit}
                >
                  <div className="space-y-1">
                    <label htmlFor="email">
                      {t('password_reset.email_label')}
                    </label>
                    <input
                      id="email"
                      name="email"
                      type="email"
                      autoComplete="email"
                      value={email}
                      onChange={(e) => {
                        setEmail(e.target.value);
                        setError(null);
                      }}
                      required
                    />
                  </div>

                  <div className="flex justify-center">
                    {isLoading ? (
                      <Loader className="w-10 h-10 text-black" />
                    ) : (
                      <input
                        className="primary purple w-full"
                        type="submit"
                        value={t('password_reset.submit_button')}
                      />
                    )}
                  </div>

                  {error && (
                    <div className="p-4 flex gap-4 items-center bg-red-50 text-red-700">
                      <ErrorIcon className="shrink-0" />
                      {error}
                    </div>
                  )}
                </form>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
